/**
 * Smartcard Reader Utility für USB-Lesegeräte
 * Unterstützt mehrere Geräte über VID/PID und Smart Card USB Klasse
 */

export interface SmartcardReaderCallbacks {
  onData: (data: string) => void;
  onError: (error: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

class SmartcardReader {
  private device: USBDevice | null = null;
  private callbacks: SmartcardReaderCallbacks | null = null;
  private isReading = false;

  async requestDevice(): Promise<boolean> {
    try {
      // Filter für verschiedene Smartcard-Lesegeräte
      const filters: any[] = [
        // Spezifische VID/PID
        { vendorId: 0xae68, productId: 0x8001 },   // ACS/Standard Reader
        { vendorId: 0x17ef, productId: 0x30ca },   // Ceto/Lenovo
        
        // USB Smart Card Class (Class 0x0B, Subclass 0x00)
        { classCode: 0x0b, subclassCode: 0x00, protocolCode: 0x00 }
      ];
      
      console.log('🔗 WebUSB: Fordere Geräteauswahl-Dialog an...');
      const devices = await navigator.usb.requestDevice({ filters });
      this.device = devices;
      console.log(`✅ WebUSB: Gerät ausgewählt - VID: 0x${devices.vendorId.toString(16)}, PID: 0x${devices.productId.toString(16)}`);
      return true;
    } catch (e: any) {
      if (e.name === 'NotFoundError') {
        console.error('❌ Kein Gerät ausgewählt. Bitte:\n1. Überprüfe, dass das USB-Smartcard-Lesegerät angeschlossen ist\n2. Wähle dein Gerät aus dem Browser-Dialog');
      } else if (e.name === 'SecurityError') {
        console.error('❌ SecurityError: WebUSB funktioniert nur auf HTTPS oder localhost');
      } else {
        console.error('❌ Fehler:', e.message);
      }
      return false;
    }
  }

  async connect(callbacks: SmartcardReaderCallbacks): Promise<boolean> {
    if (!this.device) {
      const found = await this.requestDevice();
      if (!found) return false;
    }

    try {
      this.callbacks = callbacks;

      console.log('📂 Öffne Gerät...');
      await this.device!.open();
      
      if (this.device!.configuration === null) {
        console.log('⚙️ Setze Konfiguration...');
        await this.device!.selectConfiguration(1);
      }

      const iface = this.device!.configurations[0].interfaces[0];
      const ifaceNumber = iface.interfaceNumber;

      console.log(`📌 Beanspruche Interface ${ifaceNumber}...`);
      await this.device!.claimInterface(ifaceNumber);

      console.log('✅ Verbindung erfolgreich!');
      callbacks.onConnected?.();
      this.startReading();
      return true;
    } catch (e: any) {
      const msg = `Verbindungsfehler: ${e.message}`;
      console.error(msg);
      callbacks.onError(msg);
      return false;
    }
  }

  private async startReading() {
    if (!this.device || !this.callbacks) return;

    this.isReading = true;

    try {
      while (this.isReading && this.device.opened) {
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 2000)
        );

        try {
          const data = await Promise.race([
            this.device.transferIn(1, 64),
            timeout
          ]);

          if (data.data && data.data.byteLength > 0) {
            const decoder = new TextDecoder();
            const text = decoder.decode(data.data).trim();
            
            if (text.length > 0) {
              console.log(`📥 Empfangen: ${text}`);
              this.callbacks!.onData(text);
            }
          }
        } catch (e: any) {
          if (e.message === 'Timeout') {
            await new Promise(r => setTimeout(r, 100));
          } else {
            throw e;
          }
        }
      }
    } catch (e: any) {
      if (this.callbacks) {
        this.callbacks.onError(`Lesefehler: ${e.message}`);
      }
    }
  }

  async disconnect(): Promise<void> {
    this.isReading = false;

    if (this.device?.opened) {
      try {
        const iface = this.device.configurations[0].interfaces[0];
        await this.device.releaseInterface(iface.interfaceNumber);
        await this.device.close();
        this.callbacks?.onDisconnected?.();
      } catch (e) {
        console.error('Fehler beim Trennen:', e);
      }
    }

    this.device = null;
    this.callbacks = null;
  }

  isConnected(): boolean {
    return this.device?.opened ?? false;
  }
}

export { SmartcardReader };
export type { SmartcardReaderCallbacks };
